// =============================================================================
// CallSession — per-call LLM cost analytics entity
// =============================================================================
// Source of truth for cost metrics across ALL calls (ticket-creating OR not).
// Written by the cost-tracking persister on call-end (endSession, end_call tool,
// twilio redirect, or TTL sweeper finalize).
//
// Linked to LocalTicket by `callUuid` (one-to-one when applicable; null for
// abandoned/redirected calls that never produced a ticket).
//
// Tokens are estimated via char-based heuristic (~±10% vs actual Azure billing).
// `estimationMethod` field is for future tokenizer migration.
//
// PII safety: `costByStageJSON` contains aggregates only — NO message snippets,
// NO transcripts. 90-day TTL recommended at the collection level.
// =============================================================================

import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';

export const CALL_OUTCOMES = [
  'in-progress', // call still ongoing (upserted on every turn, finalized on call-end)
  'ticket-created', // create_new_ticket happy path
  'ended-gracefully', // 🆕 end_call tool fired (caller said goodbye, bot ended call)
  'redirected', // 🆕 twilio_redirect_to_human tool fired
  'abandoned', // caller hung up without graceful end (TTL fallback)
  'failed', // 🆕 Azure 429 / content_filter / engine error — distinguish from caller-hangup
  'incomplete', // (reserved) future use
  'unknown',
] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number];

@Entity()
export class CallSession extends MongoEntity {
  static override _class = 'CallSession';
  static override _collectionName = 'call_sessions';

  constructor(data: PartialEntityData<CallSession>) {
    super(data);
  }

  // ─── Identity ───────────────────────────────────────────────────────────
  @Field(() => String) sessionID: Maybe<string>;
  @Field(() => String) callUuid: Maybe<string>;
  @Field(() => String) phoneNumber: Maybe<string>;
  /** 🆕 B1 - the number the telephony leg delivered. Null on a browser call. */
  @Field(() => String) carrierPhoneNumber: Maybe<string>;
  /** 🆕 B1 - the number the caller spoke and we looked up in the CRM. */
  @Field(() => String) spokenPhoneNumber: Maybe<string>;
  /** 🆕 B1 - phone or browser. Absent on rows written before ZW_CALL_ORIGIN. */
  @Field(() => String) callOrigin: Maybe<string>;
  @Field(() => String) effectiveFlow: Maybe<string>;
  /** Immutable non-secret deployment snapshot captured when this call begins. */
  @Field(() => String) deploymentIdentityJSON: Maybe<string>;
  @Field(() => String) flowReleaseId: Maybe<string>;
  @Field(() => String) flowProjectHash: Maybe<string>;
  @Field(() => Int) flowSettingsRevision: Maybe<number>;
  /** 🆕 TASK 4 - cached plain-language summary of the call, generated on demand. */
  @Field(() => String) analysisText: Maybe<string>;
  @Field(() => Date) analysisAt: Maybe<Date>;
  @Field(() => String) language: Maybe<'de' | 'en'>;

  // ─── Timing ─────────────────────────────────────────────────────────────
  @Field(() => Date) startedAt: Maybe<Date>;
  @Field(() => Date) endedAt: Maybe<Date>;

  // ─── Outcome ────────────────────────────────────────────────────────────
  /** Final disposition of the call. Drives funnel analytics in the UI. */
  @Field(() => String) outcome: Maybe<CallOutcome>;
  /** Which agent was active when the call ended (intent / auth-path-* / ticket-creation / existing-ticket / unknown). */
  @Field(() => String) finalAgent: Maybe<string>;
  /** Optional: ticket number if this call produced one (links back to LocalTicket.ticketNumber). */
  @Field(() => String) linkedTicketNumber: Maybe<string>;

  // ─── Cost & token totals (estimated) ────────────────────────────────────
  @Field(() => Int) totalTurns: Maybe<number>;
  @Field(() => Int) totalInputTokens: Maybe<number>;
  @Field(() => Int) totalOutputTokens: Maybe<number>;
  @Field(() => Float) totalCostUSD: Maybe<number>;
  @Field(() => Float) totalCostEUR: Maybe<number>;

  /**
   * Per-agent-stage breakdown stored as JSON string for flexibility.
   * Shape: { 'intent': { usd, eur, inputTokens, outputTokens, turns },
   *          'auth-path-c-22': {...}, 'ticket-creation': {...}, ... }
   */
  @Field(() => String) costByStageJSON: Maybe<string>;

  /**
   * Token estimation method used at write time. Allows future tokenizer
   * migration (e.g., 'mistral-spm-v1') without conflating data.
   * Current: 'char-heuristic-v1' (~±10% accuracy).
   */
  @Field(() => String) estimationMethod: Maybe<string>;

  /**
   * Whether output tokens were actually captured from the Azure streaming
   * response (true) or estimated via heuristic (false). For MVP, this is
   * false until we wire usage capture from token-js completion events.
   */
  @Field(() => Boolean) outputTokensActual: Maybe<boolean>;

  /**
   * 🆕 When offline re-tokenization last upgraded this row (null = never).
   * Cost-accuracy tier is carried by `estimationMethod`:
   *   'char-heuristic-v1' (live estimate) → 'retokenized-v1' (output re-tokenized
   *   offline) → 'provider-exact-v1' (OpenRouter /generation exact billed cost).
   */
  @Field(() => Date) retokenizedAt: Maybe<Date>;

  // ─── 🆕 Per-call voice-infra cost breakdown (computed offline at retokenize) ──
  // Twilio + Deepgram derived from durationMs (per-minute billing); Cartesia from
  // the bot's spoken characters. EUR, using the editable Preise rates. `costTotalEUR`
  // = LLM (totalCostEUR) + the three below. All null until a retokenize pass runs.
  /** Characters the bot spoke (TTS input) — drives the Cartesia cost. */
  @Field(() => Int) spokenChars: Maybe<number>;
  /** Twilio telephony cost for this call (EUR), from call duration. */
  @Field(() => Float) costTwilioEUR: Maybe<number>;
  /** Deepgram STT cost for this call (EUR), from call duration. */
  @Field(() => Float) costDeepgramEUR: Maybe<number>;
  /** Cartesia TTS cost for this call (EUR), from spoken characters. */
  @Field(() => Float) costCartesiaEUR: Maybe<number>;
  /** Grand total (EUR): LLM + Twilio + Deepgram + Cartesia. */
  @Field(() => Float) costTotalEUR: Maybe<number>;

  /**
   * 🆕 Optional human-readable error reason for `outcome:'failed'` outcomes.
   * E.g., 'azure-rate-limit', 'azure-content-filter', 'azure-timeout',
   * 'engine-throw', 'unknown'. Distinguishes "Azure broken" from
   * "caller hung up" in the dashboard.
   */
  @Field(() => String) errorReason: Maybe<string>;

  /**
   * 🆕 Call duration in milliseconds (endedAt - startedAt).
   * Computed at persist time for cost-per-minute analytics.
   */
  @Field(() => Int) durationMs: Maybe<number>;

  // ─── Test Lab fields ────────────────────────────────────────────────────
  /** True for synthetic calls initiated from the Test Lab UI. CostAnalytics
   *  filters these out via BooleanAspect to keep production analytics clean. */
  @Field(() => Boolean) isTestCall: Maybe<boolean>;
  /** Test scenario name (e.g. 'email', 'cas-end-to-end'). */
  @Field(() => String) testScenarioName: Maybe<string>;
  /** Profile ID used to pre-seed the synthetic caller (from TEST_CALLERS const). */
  @Field(() => String) testCallerProfile: Maybe<string>;
  /** JSON-stringified array of TestVerdict objects emitted during the call. */
  @Field(() => String) testVerdictsJSON: Maybe<string>;
  /** AdornisUser._id that initiated this test call (for rate-limit + audit). */
  @Field(() => String) startedByUserID: Maybe<string>;

  // ─── Tester feedback (overwrite-able, one note per session) ────────────
  /** Name entered by the person leaving feedback. Legacy stored names remain unchanged. */
  @Field(() => String) testerName: Maybe<string>;
  /** Free-text feedback from the tester. DE or EN. Overwritten on each save. */
  @Field(() => String) testerNotes: Maybe<string>;
  /** When the feedback was last saved (for sorting in the feedback list). */
  @Field(() => Date) testerNotesUpdatedAt: Maybe<Date>;
  /** Workflow state of the existing note; absent legacy values display as pending. */
  @Field(() => String) testerFeedbackStatus: Maybe<string>;
}

// =============================================================================
// Startup backfill — set isTestCall:false on existing CallSession rows
// =============================================================================
// MUST run BEFORE the BooleanAspect filter is added to CostAnalytics, else
// existing rows where isTestCall field is missing would vanish from the table
// (Mongo $eq:false does not match missing fields).
//
// Idempotent: countDocuments returns 0 after first run → no-op.
// =============================================================================

import { getCollection } from '@adornis/baseql/server/collections.js';
import { logger } from '@adornis/base/logging.js';

export async function ensureCallSessionBackfilled(): Promise<void> {
  const coll = await getCollection(CallSession);
  const missing = await coll.countDocuments({ isTestCall: { $exists: false } });
  if (missing === 0) {
    logger.info({}, 'CallSession: isTestCall backfill already complete');
    return;
  }
  logger.warn({ missing }, 'CallSession: backfilling isTestCall:false on legacy rows');
  const result = await coll.updateMany({ isTestCall: { $exists: false } }, { $set: { isTestCall: false } });
  logger.info({ modified: result.modifiedCount }, 'CallSession: backfill complete');
}
